import { MRT_ColumnDef } from 'mantine-react-table'
import { Text, Tooltip } from '@mantine/core'
import { ReactNode } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { formatDateToShow, stringToDate } from 'app/utils/util-functions'
import { TradeListRow } from './utils/trade-list-rows'
import SellerPositionHoverCard from 'app/views/components/seller-position-hover-card/seller-position-hover-card'
import IonIcon from '@reacticons/ionicons'
import HoldIndicator, { holdsToEntries } from 'app/views/components/hold-indicator'
import { getUnreadCounts } from 'app/state/ducks/trade-notes/selectors'
import { masterTradeStatusLabel } from './utils/master-trade-status'

export const statusColors: { [key: string]: string } = {
    open: '#287A26',
    settled: '#EE5959',
    closed: '#EE5959',
    draft: '#757373',
    canceled: '#E8006E',
    amendment: '#8338EC',
    terminated: '#E8006E',
};

// Single lookup for facility status display + color. Indexed by both Redux convention
// names ('Approved', 'Submitted') and PO-allowed display names ('Active', 'Draft', etc.)
// so callers can pass either without translating first. Colors recycle from statusColors.
export const facilityStatusInfo: { [key: string]: { display: string, color: string } } = {
    Active:     { display: 'Active',     color: statusColors.open },
    Approved:   { display: 'Active',     color: statusColors.open },
    Draft:      { display: 'Draft',      color: statusColors.draft },
    Submitted:  { display: 'Draft',      color: statusColors.draft },
    Amendment:  { display: 'Amendment',  color: statusColors.amendment },
    Terminated: { display: 'Terminated', color: statusColors.canceled },
    Locked:     { display: 'Locked',     color: statusColors.settled },
};

function createEntityCell(columnType: 'myEntity' | 'counterparty') {
    return ({ row }: { row: any }) => {
        const navigate = useNavigate();
        const entityName = columnType === 'myEntity' ? row.original.entityName : row.original?.counterpartyName;
        const entityMei = columnType === 'myEntity' ? row.original.entityMei : row.original?.counterpartyMei;

        const isShowingSeller = columnType === 'myEntity'
            ? row.original.myEntity === 'Sell'
            : row.original.myEntity === 'Buy';

        const facilityIds: string[] = row.original.facilityIds ?? [];

        // only show seller position hover card if trades are draft or open status
        const subtradeStatus = (row.original.status ?? '').toLowerCase();
        const shouldShowHoverCard = isShowingSeller && (subtradeStatus === 'open' || subtradeStatus === 'draft');

        const nameElement = (
            <Text
                className='referenceDataText'
                style={{
                    textAlign: 'start',
                    cursor: 'pointer',
                }}
                onClick={() => {
                    const entity = columnType === 'myEntity'
                        ? row.original.myEntity === 'Sell'
                            ? row.original.sellerId
                            : row.original.buyerId : row.original.myEntity === 'Buy'
                            ? row.original.sellerId : row.original.buyerId;
                    if (!entity?.id)
                        return

                    navigate(`/entitymanagement/entity/${entity.id}/Active`)
                }}>{entityName}</Text>
        );

        return (
            <div>
                {shouldShowHoverCard ? (
                    <SellerPositionHoverCard
                        sellerId={row.original.sellerId?.id || ''}
                        facilityIds={facilityIds}
                    >
                        {nameElement}
                    </SellerPositionHoverCard>
                ) : (
                    nameElement
                )}
                <TableRowText text={`${entityMei}`} textAlign='start' />
            </div>
        );
    };
}

function TableHead({ header, textAlign }: { header: string | ReactNode, textAlign?: 'start' | 'end' | 'center' }) {
    return (
        <Text
            style={{
                fontFamily: 'Plus Jakarta Sans',
                fontSize: '14px',
                fontWeight: 700,
                lineHeight: '24px',
                color: '#413E3E',
                textAlign: textAlign ?? 'center',
                display: 'block',
            }}
        >
            {header}
        </Text>
    )
}

function TableRowText({ text, textAlign, whiteSpace, color, dataTestId }: { text: ReactNode, textAlign?: 'start' | 'center' | 'end', whiteSpace?: 'normal' | 'nowrap' | 'pre' | 'pre-wrap' | 'pre-line', color?: string, dataTestId?: string }) {
    return (
        <Text
            data-test-id={dataTestId}
            style={{
                fontFamily: 'Plus Jakarta Sans',
                fontSize: '14px',
                fontWeight: 600,
                lineHeight: '19px',
                color: color ?? '#413E3E',
                textAlign: textAlign ?? 'center',
                whiteSpace: whiteSpace ?? 'normal',
            }}
        >
            {text}
        </Text>
    )
}


export const columnDefs: MRT_ColumnDef<TradeListRow>[] = [
    // ADR_LOS-028: entry point to the trade's ACTIVITY STREAM tab, mirroring the Signing
    // Portal's Stream column. The count badge is read-state-backed (read-state design):
    // portal-authored notes with no read receipt, summed across the master trade's sub-trades.
    {
        size: 40,
        id: 'stream',
        header: 'Stream',
        enableSorting: false,
        Header: () => <TableHead header={'Stream'} textAlign='center' />,
        Cell: ({ row }) => {
            const navigate = useNavigate();
            const unreadCounts = useSelector(getUnreadCounts);
            const openStream = () => navigate(`/tradeentry/${row.original.id}/activity`);
            // Unread across the master trade's sub-trades (read-state design). Zero = no badge.
            const unread = unreadCounts.byMasterTrade[row.original.id ?? ''] ?? 0;
            return (
                <Tooltip label="Activity Stream" withinPortal>
                    <div
                        role="button"
                        tabIndex={0}
                        aria-label="Open activity stream"
                        style={{ cursor: 'pointer', display: 'flex', justifyContent: 'center' }}
                        onClick={openStream}
                        onKeyDown={event => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault()
                                openStream()
                            }
                        }}
                    >
                        <span style={{ position: 'relative', display: 'inline-flex' }}>
                            {/* The prototype's Stream icon — a list-checks glyph, not a chat bubble. */}
                            <svg viewBox="0 0 24 24" fill="none" stroke="#32439A" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" style={{ width: 22, height: 22, flex: '0 0 auto' }}>
                                <path d="m3 7 2 2 4-4M13 7h8M13 11h6M3 16l2 2 4-4M13 16h8M13 20h6" />
                            </svg>
                            {unread > 0 && (
                                <span
                                    style={{
                                        position: 'absolute',
                                        top: -5,
                                        right: -8,
                                        minWidth: 18,
                                        height: 18,
                                        padding: '0 4px',
                                        borderRadius: 999,
                                        background: '#9A2222',
                                        color: '#FFFFFF',
                                        fontSize: 11,
                                        fontWeight: 700,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {unread}
                                </span>
                            )}
                        </span>
                    </div>
                </Tooltip>
            );
        },
    },
    {
        size: 60,
        header: 'Trade Ids',
        mantineTableHeadCellProps: {
            className: 'cell-width-60',
        },
        Header: () => (
            <div>
                <TableHead header={'Trade ID'} textAlign='start' />
                <TableHead header={'Internal ID'} textAlign='start' />
            </div>
        ), Cell: ({ row }) => {
            const tradeId = row.original.tradeId;
            const clientId = row.original.clientId;
            const id = row.original.id
            const navigate = useNavigate();
            return (
                <div>
                    <Text
                        itemType='link'
                        onClick={() =>
                            navigate(`/tradeentry/${id}`)
                        }
                        className='referenceDataText'
                        style={{
                            textAlign: 'start',
                        }}>{tradeId}</Text>
                    <TableRowText text={`${clientId ?? ''}`} textAlign='start' />
                </div>
            );
        },
    },
    {
        size: 60,
        accessorKey: 'status',
        header: 'Status',
        mantineTableHeadCellProps: {
            className: 'cell-width-60',
        },
        Header: <TableHead header={'Status'} textAlign='center' />,
        Cell: ({ cell }) => {
            const label = masterTradeStatusLabel(cell.getValue() as string);
            const status = label.toLowerCase();

            return (

                <Text style={{
                    color: statusColors[status] || '#000000',
                    textTransform: 'capitalize',
                    fontFamily: 'Plus Jakarta Sans',
                    fontSize: '14px',
                    fontWeight: 600,
                    lineHeight: '19px',
                }}>{label}</Text>
            );
        },
    },
    {
        header: 'Trade Details',
        size: 100,
        mantineTableHeadCellProps: {
            className: 'cell-width-100',
        },
        Header: () => (
            <div style={{ width: "100%" }}>
                <TableHead header={'Trade Details'} textAlign='center' />
                <TableHead header={'Transfer - Payment'} textAlign='center' />
            </div>
        ), Cell: ({ row }) => {
            return (
                <>
                    <TableRowText text={row.original.tradeDetails} textAlign='center' />
                    <TableRowText text={`${row.original.transferType}-${row.original.paymentType}`} textAlign='center' />
                </>
            );
        },
    },
    {
        header: 'Trade Action',
        size: 60,
        Header: () => (
            <div>
                <TableHead header={'Trade'} textAlign='start' />
                <TableHead header={'Action'} textAlign='start' />
            </div>
        ), Cell: ({ row }) => {
            return (
                <div>
                    <TableRowText text={`${row.original.myEntity}`} textAlign='start' />
                </div>
            );
        },
    },
    {
        size: 100,
        header: 'Trade Date',
        mantineTableHeadCellProps: {
            className: 'cell-width-100',
        },
        Header: () => (
            <div style={{ width: "100%" }}>
                <TableHead header={'TD'} textAlign='start' />
                <TableHead header={<>SD&nbsp;&nbsp;<span style={{ color: '#9567af' }}>(T+)</span></>} textAlign='start' />
            </div>
        ), Cell: ({ row }) => {
            return (
                <div>
                    <TableRowText text={`${formatDateToShow(stringToDate(row.original.tradeDate ?? ''))}`} textAlign='start' />
                    {(row.original.hasMultipleSDs || row.original.settlementDate) && <TableRowText whiteSpace='nowrap' text={row.original.hasMultipleSDs ? 'Multiple SD' : <>{formatDateToShow(row.original.settlementDate)} <span style={{ color: '#9567AF' }}>({row.original.tPlus})</span></>} textAlign='start' dataTestId={row.original.hasMultipleSDs ? 'multiple-settlement-dates' : 'settlement-date'} />}
                </div>
            );
        },
    },
    {
        size: 150,
        header: 'My Entity',
        mantineTableHeadCellProps: {
            className: 'cell-width-150',
        },
        Header: () => (
            <div style={{ width: "100%" }}>
                <TableHead header={'My Entity'} textAlign='start' />
                <TableHead header={'MEI'} textAlign='start' />
            </div>
        ),
        Cell: createEntityCell('myEntity'),
    },
    {
        size: 150,
        header: 'Counterparty',
        mantineTableHeadCellProps: {
            className: 'cell-width-150',
        },
        Header: () => (
            <div style={{ width: "100%" }}>
                <TableHead header={'Counterparty'} textAlign='start' />
                <TableHead header={'MEI'} textAlign='start' />
            </div>
        ),
        Cell: createEntityCell('counterparty'),
    },
    {
        size: 150,
        header: 'Deal & Facilities',
        mantineTableHeadCellProps: {
            className: 'cell-width-150',
        },
        Header: () => (
            <div style={{ width: "100%" }}>
                <TableHead header={'Deal (CUSIP)'} textAlign='start' />
                <TableHead header={'Facilities (CUSIP)'} textAlign='start' />
            </div>
        ),
        Cell: ({ row }) => {
            const navigate = useNavigate();

            // The projection folds the trade's facilities server-side: a count,
            // and the one name/cusip when there is exactly one.
            const { facilityCount, facilityName, facilityCusip } = row.original;

            const facilityTextObject = facilityCount > 1
                ? (<Text
                    className='multipleTradeFacilityText'
                    style={{ whiteSpace: 'nowrap', overflow: 'hidden' }}
                >
                    Multiple Facilities {' '}
                    <span
                        className='multipleTradeFacilityTextCount'
                    >
                        ({facilityCount})
                    </span>
                </Text>)
                : (
                    <Text
                        className='singleTradeFacilityText'
                        style={{ whiteSpace: 'nowrap', overflow: 'hidden' }}
                    >
                        {(facilityName ?? '') + ' (' + (facilityCusip || 'N/A') + ')'}
                    </Text>
                )

            return (
                <div>
                    <Text
                        style={{
                            cursor: 'pointer',
                            textAlign: 'start',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden'
                        }}
                        onClick={() => {
                            if (!row.original.dealId?.id)
                                return
                            navigate(`/dealmanagement/deal/${row.original.dealId?.id}/Approved`)
                        }}
                        className='referenceDataText'
                    >
                        {row.original.dealName + ' ' + row.original.dealCusip}
                    </Text>
                    {facilityTextObject}
                </div>
            );
        },
    },
    {
        size: 80,
        header: 'Facility Status',
        mantineTableHeadCellProps: { className: 'cell-width-80' },
        Header: () => <div style={{ width: '100%' }}><TableHead header={'Facility Status'} textAlign='start' /></div>,
        Cell: ({ row }) => {
            // `facilityStatus` is one template status, or 'Multiple' when the
            // trade's facilities disagree; the count shown is how many distinct
            // labels they carry (two Approved + one Closed = 2), as the page did.
            const { facilityStatus, facilityStatusCount } = row.original;
            if (!facilityStatus) return null;
            if (facilityStatus === 'Multiple') {
                return (
                    <Text className='multipleTradeFacilityText' style={{ whiteSpace: 'nowrap', textAlign: 'start' }}>

                        Multiple{' '}
                        <span className='multipleTradeFacilityTextCount'>({facilityStatusCount})</span>
                    </Text>
                );
            }
            const single = facilityStatusInfo[facilityStatus]?.display ?? facilityStatus;
            return (
                <Text
                    style={{
                        color: facilityStatusInfo[single]?.color ?? '#413E3E',
                        fontFamily: 'Plus Jakarta Sans',
                        fontSize: '14px',
                        fontWeight: 600,
                        lineHeight: '19px',
                        textAlign: 'start',
                    }}
                >
                    {single}
                </Text>
            );
        },
    },
    {
        size: 60,
        header: 'Agent',
        mantineTableHeadCellProps: {
            className: 'cell-width-60',
        },
        Header: () => (
            <div style={{ width: "100%" }}>
                <TableHead header={'Agent'} textAlign='start' />
            </div>
        ), Cell: ({ row }) => {
            return (
                <div>
                    <TableRowText text={`${row.original.agent}`} textAlign="start" />
                </div>
            );
        },
    },
    {
        size: 100,
        header: 'Trade Amount & Current Amount',
        mantineTableHeadCellProps: {
            className: 'cell-width-100',
        },
        Header: () => (
            <div style={{ width: "100%" }}>
                <TableHead header={'Traded Amount'} textAlign='end' />
                <TableHead header={'Current Amount'} textAlign='end' />
            </div>
        ), Cell: ({ row }) => {
            const { tradedAmount, currentAmount, uniqueCurrencies, tradedAmountRaw, currentAmountRaw } = row.original;
            const hasAmountChange = tradedAmountRaw && currentAmountRaw && tradedAmountRaw !== currentAmountRaw;
            const isPaydown = hasAmountChange && tradedAmountRaw > currentAmountRaw;

            return (
                <div style={{ position: 'relative', paddingRight: '6px' }}>
                    <div className="tradeCellFlexEnd">
                        <TableRowText text={tradedAmount} textAlign="end" />
                    </div>
                    <div className="tradeCellFlexEnd">
                        <TableRowText
                            textAlign="end"
                            text={currentAmount}
                            color={hasAmountChange ? (isPaydown ? '#EE5959' : '#287A26') : undefined}
                        />
                        {uniqueCurrencies > 1 && <Text
                            className='multipleTradeFacilityText'
                            style={{ textAlign: 'right' }}
                        >
                            Multiple Currencies {' '}
                            <span
                                className='multipleTradeFacilityTextCount'
                            >
                                ({uniqueCurrencies})
                            </span>
                        </Text>}
                    </div>
                    {hasAmountChange && (
                        <IonIcon
                            name={isPaydown ? "arrow-down-outline" : "arrow-up-outline"}
                            style={{
                                color: isPaydown ? '#EE5959' : '#287A26',
                                fontSize: '20px',
                                position: 'absolute',
                                top: '8%',
                                right: '-13px'
                            }}
                        />
                    )}
                </div>
            );
        },
    },
    {
        size: 100,
        header: 'Price',
        mantineTableHeadCellProps: {
            className: 'cell-width-100',
        },
        Header: () => (
            <div style={{ width: "100%" }}>
                <TableHead header={'Price'} textAlign='end' />
            </div>
        )
        ,
        Cell: ({ row }) => {
            const { price, priceCount } = row.original;
            const priceStyle = {
                color: priceCount === 1 ? '#413E3E' : '#9567AF',
                fontWeight: 600,
                fontSize: '14px',
                lineHeight: '19px',
                fontFamily: 'Plus Jakarta Sans',
                textAlign: 'right' as any
            };

            const countStyle = {
                color: '#EE5959',
                fontWeight: 600,
                fontSize: '14px',
                lineHeight: '19px',
                fontFamily: 'Plus Jakarta Sans',
            };

            return (
                <div>
                    {priceCount === 1 ? (
                        <Text style={priceStyle} >
                            {price}
                        </Text>
                    ) : (
                        <Text style={priceStyle}>
                            Mult{' '}
                            <span style={countStyle}>
                                ({priceCount})
                            </span>
                        </Text>
                    )}
                </div>
            );
        },
    },
    {
        size: 60,
        header: 'Lock',
        mantineTableHeadCellProps: {
            className: 'cell-width-60',
        },
        Header: <TableHead header={'Lock'} textAlign='center' />,
        Cell: ({ row }) => {
            const tradeId = row.original.id
            if (!tradeId || row.original.status !== 'Open' || !row.original.hasSignableDocs) return null
            return (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <HoldIndicator
                        holds={holdsToEntries(row.original.holds ?? [], 'Trade', () => undefined)}
                        variant="auto"
                        interaction="click"
                        layout="accordion"
                        ariaLabel="Trade lock status"
                    />
                </div>
            )
        },
    },
]
